import { useEffect, useId, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useDragControls, useReducedMotion, type PanInfo } from 'framer-motion';
import { useIsMobileViewport } from '@/lib/platform';

/**
 * Modal padrão do app — uma única superfície para todos os diálogos.
 *
 * - **Mobile** (< sm): bottom sheet branco que entra deslizando de baixo e sai
 *   deslizando para baixo. Fecha por: arrastar para baixo, clicar fora, X ou Esc.
 * - **Desktop** (>= sm): card branco centralizado com fade + zoom suave.
 *
 * z-index `1200` fica ACIMA do BottomNav (`1000`) e do seu sheet (`1100`), então
 * o modal sempre sobrepõe o menu.
 *
 * O `open` deve ser controlado pelo pai (renderize sempre, não com `{cond && …}`)
 * para que a animação de saída rode antes de desmontar.
 */

type Size = 'sm' | 'md' | 'lg' | 'xl';

const sizeClasses: Record<Size, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-2xl',
};

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Linha de ações fixa no rodapé (Cancelar/X + ação primária). */
  footer?: ReactNode;
  size?: Size;
  /** Permite fechar ao clicar no backdrop / Esc. Default `true`. Desligue durante um submit. */
  dismissable?: boolean;
  /** Esconde o botão X do cabeçalho. */
  hideClose?: boolean;
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissable = true,
  hideClose = false,
}: ModalProps) {
  const titleId = useId();
  const descId = useId();
  const isMobile = useIsMobileViewport();
  const reduce = useReducedMotion();
  const dragControls = useDragControls();

  const requestClose = () => {
    if (dismissable) onClose();
  };

  // Esc para fechar + trava o scroll do body enquanto aberto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, dismissable, onClose]);

  if (typeof document === 'undefined') return null;

  const startDrag = (e: ReactPointerEvent) => {
    if (isMobile) dragControls.start(e);
  };

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 120 || info.velocity.y > 500) requestClose();
  };

  const panelMotion = isMobile
    ? { initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' } }
    : { initial: { opacity: 0, scale: 0.96, y: 8 }, animate: { opacity: 1, scale: 1, y: 0 }, exit: { opacity: 0, scale: 0.96, y: 8 } };

  const transition = reduce ? { duration: 0 } : { duration: 0.28, ease: [0.32, 0.72, 0, 1] as const };

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[1200] flex items-end justify-center sm:items-center sm:p-4">
          <motion.div
            className="absolute inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.2 }}
            onClick={requestClose}
            aria-hidden="true"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined}
            className={`relative flex w-full ${sizeClasses[size]} max-h-[90dvh] flex-col overflow-hidden rounded-t-2xl border border-line bg-white shadow-2xl sm:rounded-2xl`}
            initial={panelMotion.initial}
            animate={panelMotion.animate}
            exit={panelMotion.exit}
            transition={transition}
            drag={isMobile ? 'y' : false}
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={onDragEnd}
          >
            <header
              onPointerDown={startDrag}
              className="shrink-0 select-none border-b border-line px-5 pt-4 pb-3 sm:cursor-default touch-none"
            >
              <div className="mx-auto -mt-1 mb-3 h-1.5 w-10 rounded-full bg-line sm:hidden" aria-hidden="true" />
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 id={titleId} className="font-display text-lg font-semibold text-ink">
                    {title}
                  </h2>
                  {description && (
                    <p id={descId} className="mt-1 text-sm text-muted">
                      {description}
                    </p>
                  )}
                </div>
                {!hideClose && (
                  <button
                    type="button"
                    onClick={requestClose}
                    aria-label="Fechar"
                    className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-faint hover:text-ink active:bg-line"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                      <path d="M6 6l12 12M6 18L18 6" />
                    </svg>
                  </button>
                )}
              </div>
            </header>

            {children != null && children !== false && (
              <div
                className={`min-h-0 flex-1 overflow-y-auto px-5 pt-4 ${
                  footer ? 'pb-4' : 'pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4'
                }`}
              >
                {children}
              </div>
            )}

            {footer && (
              <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-line px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:pb-3">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
